<template>
   <RequestLoader :isLoading="isLoading" data-cy="profile-phone-block">
      <div class="block" data-cy="profile-phone-v1">
         <SimpleSelect
            class="country-code"
            :selectedOption="userCountryCode"
            :label="$t('validation.phone_number.country_code')"
            :options="countryCodeOptions"
            @option-selected="onCountryCodeUpdated"
            @change="$v.$touch"
         />
         <NumberTextBox
            class="phone-number"
            v-model="userPhoneNumber"
            :hasError="isPhoneNumberValid"
            :placeholder="phoneNumberPlaceHolder"
            :label="$t('validation.phone_number.value')"
            autocomplete="tel-national"
            @change="processInputs"
         />
      </div>
      <InvalidInputsList :errorList="firstInvalidInput" :class="{ hidden: !showBlockErrors }" />
   </RequestLoader>
</template>

<script setup lang="ts">
import { required } from "@vuelidate/validators";
import { parsePhoneNumber } from "awesome-phonenumber";
import { useVuelidate } from "@vuelidate/core";
import countryCodesJson from "@/data/country-calling-codes";
import { providePhoneNumberExample } from "@/js/helpers/phone-number-utils";
import { useI18n } from "@/vue-i18n/vue-i18n";
import type { FlowselBlock } from "@/components/flowsel/flowsel.type";
import type { AnswerResp } from "@/data/api-layer/answersApi";
import { useUserStore } from "@/stores/userStore";
import { useApiRequest } from "@/composables/useApiRequest";

export interface ProfilePhoneAnswerData {
   phone: string;
}
export interface ProfilePhoneBlock extends FlowselBlock {
   data: {
      settings: {
         restrictedToCountries: string[];
         defaultCountryCode: string;
      };
   };
}
interface Props {
   block: ProfilePhoneBlock;
   userBlockAnswer: AnswerResp | null;
   showBlockErrors: boolean;
}

// declarations
// -----------------------------------------
const { isLoading, execRequest, loadErrors } = useApiRequest();
const emit = defineEmits(["update-valid", "update-answer", "enter-trigger-next"]);
const userStore = useUserStore();
const { t: $t } = useI18n();

const userCountryCode = ref("");
const userPhoneNumber = ref("");

const props = defineProps<Props>();

// Lifecycle
// -----------------------------------------
onMounted(async () => {
   if (props.userBlockAnswer) {
      initialiseComponent(props.userBlockAnswer.data?.phone);
      isLoading.value = false; // no data to fetch so we can complete the loading
   }
   // no user block answer > try to fetch user profile data to initialise the component with
   else {
      await execRequest(() => userStore.setUser());

      if (loadErrors.value.length === 0) {
         initialiseComponent(userStore.user?.phone);
      }
   }
});

// computed
// -----------------------------------------
const countryCodePlusNumber = computed(() => {
   if (!userCountryCode.value || !userPhoneNumber.value) return null;

   // ignore leading zero in the national number
   const userNumberNoZero = userPhoneNumber.value.replace(/^0/, "");

   // example: +44 1234567890
   return `+${userCountryCode.value} ${userNumberNoZero}`;
});

const countryCodeOptions = computed(() => {
   let options;

   if (
      !props.block?.data?.settings?.restrictedToCountries ||
      props.block.data.settings.restrictedToCountries.length === 0
   ) {
      options = countryCodesJson;
   }
   // restrict to specific countries
   else {
      options = countryCodesJson.filter(({ iso2CountryCode }) =>
         props.block.data.settings.restrictedToCountries.includes(iso2CountryCode)
      );
   }

   return options.map(({ countryCallingCode, countryName }) => {
      return {
         label: `${countryName} (+${countryCallingCode})`,
         value: countryCallingCode,
      };
   });
});

const phoneNumberPlaceHolder = computed(() => {
   if (!userCountryCode.value) return;
   return providePhoneNumberExample(userCountryCode.value);
});

//
// Vuelidate &  validation
// -----------------------------------------
/** Get the error message for the given validator.
 * @param {string} validator validator type e.g 'required'
 * @param {string} property property - the data key attached to the input e.g 'countryCode'
 */
const getErrorMessage = (validator: string, property: string): string | null => {
   if (property === "userCountryCode" && validator === "required") {
      return $t("validation.phone_number.required_cc");
   } else if (property === "userPhoneNumber") {
      if (validator === "required") {
         return $t("validation.phone_number.required");
      } else if (validator === "numeric") {
         return $t("validation.phone_number.invalid_characters");
      } else if (validator === "checkPhoneNumberIsValid") {
         return $t("validation.phone_number.invalid_combination");
      }
   } else {
      return null;
   }
};

const errorList = computed(() => {
   const allErrors = $v.value.$errors.map((vError) => ({
      property: vError.$property,
      inputId: props.block.id,
      validator: vError.$validator,
      message: getErrorMessage(vError.$validator, vError.$property),
   }));

   // console.log("allErrors: ", allErrors);
   return allErrors;
});

const firstInvalidInput = computed(() => {
   if (errorList.value.length === 0) return [];

   return [errorList.value[0]];
});

// check if the phone number is valid  based on $v in vuelidate
const isPhoneNumberValid = computed(() => {
   return !!errorList.value.find((item) => item.property === "userPhoneNumber");
});

// used in the validation rules
function checkPhoneNumberIsValid(value) {
   if (userPhoneNumber.value.length > 0) {
      // awesome-phonenumber thinks that "+44 7787234342g" is valid because it ignores the invalid characters at the end
      // Because of this, we have to manually validate to only allow: numbers, spaces, (), and -
      const cleanedNumber = userPhoneNumber.value.replace(/\s+/g, " ").trim(); // Normalize spaces and trim
      const isPhoneInputValid = /^[0-9\s()-]+$/.test(cleanedNumber);

      if (!isPhoneInputValid) {
         console.warn("Invalid characters in phone number field.");
         return false; // Return false if invalid characters are found
      }

      // Check that the full phone number including country code is valid (using awesome-phonenumber parser)
      const phoneNum = parsePhoneNumber(unref(countryCodePlusNumber));

      return phoneNum?.valid || false; // Return validity or false if parsing failed
   }

   return true; // If the field is empty, treat it as valid as we test for required separately
}

const rules = {
   userCountryCode: {
      ...(props.block.isRequired && { required }),
      $autoDirty: true,
   },
   userPhoneNumber: {
      ...(props.block.isRequired && { required }),
      checkPhoneNumberIsValid,
      // numeric,
      $autoDirty: true,
   },
};
const $v = useVuelidate(rules, { userPhoneNumber, userCountryCode });

// Methods
// -----------------------------------------
/**
 * Update the inputs with any retrieved profile data
   uses awesome-phonenumber to separate the number stored in the db into parts; countryCode and national number
   awesome-phonenumber's national number will likely have leading zero, we remove it as we are displaying it alongside the country code
   and combining it with the country code to form the full number before emitting the answer
 * @param {object} userPhoneNum
 */
function initialiseComponent(userPhoneNum) {
   if (userPhoneNum) {
      try {
         const phoneNum = parsePhoneNumber(userPhoneNum);

         userCountryCode.value = phoneNum.countryCode.toString();

         // use the user entered phone number format
         const firstSpaceIndex = phoneNum.number.input.indexOf(" ");
         userPhoneNumber.value = firstSpaceIndex !== -1 ? phoneNum.number.input.slice(firstSpaceIndex + 1) : "";
      } catch {
         console.error("Could not parse users profile phone: ", userPhoneNum);
      }
   }
   // If there is no existing answer, use the defaultCountryCode if this exists
   else {
      if (props.block?.data?.settings?.defaultCountryCode) {
         userCountryCode.value = props.block.data.settings.defaultCountryCode;
      }
   }

   processInputs();
}

function onCountryCodeUpdated(code) {
   userCountryCode.value = code;

   processInputs();
}

/** validate inputs and emit if valid */
function processInputs() {
   if (!$v.value.$dirty) $v.value.$touch();

   if ($v.value.$invalid || $v.value.$error) {
      // an input is invalid or has errors
      emit("update-valid", false);
   } else {
      // all inputs are valid
      emit("update-valid", true);
      // Either emit the response in the format expected
      // Or if there is no response and that is valid (an optional question), submit null
      const answer = !countryCodePlusNumber.value ? null : { phone: countryCodePlusNumber.value };
      emit("update-answer", answer);
   }
}
</script>

<style scoped lang="scss">
.block {
   display: flex;
   justify-content: space-between;
   padding: 0.5em 0;
   gap: 10px;
}

// equal width for both inputs
.country-code,
.phone-number {
   flex: 1;
}
</style>
